import { forwardRef } from 'react';

import { useRender } from '@base-ui/react/use-render';

import { cn } from '../utils';
import { buttonRecipe } from './Button.recipe';
import { ButtonContent } from './ButtonContent';

import type { ButtonLinkProps } from './types';

/**
 * Design System 2.0 link that looks like a button.
 *
 * A navigation control that LOOKS like a button must still BE a link: right-click, middle-click,
 * cmd-click, "copy link address" and the screen-reader link role all come from the anchor and
 * cannot be reproduced on a `<button>`.
 *
 * Deliberately not built on Base UI's `Button` with `render={<a />}`. `useButton` detects the
 * non-native element and compensates — it spreads `role="button"` and synthesises a click from the
 * Space key — which would ship an anchor announced as a button. Base UI's own docs say not to do
 * this. Instead the styling is reused directly: `buttonRecipe` is a plain `cva` class list with no
 * element assumptions, so it applies to an anchor unchanged.
 *
 * `no-underline` is explicit because Tailwind's Preflight is deliberately omitted (see
 * `packages/design-system/src/styles/design-system.css`), so the user-agent underline on `a[href]` is still live.
 * Text colour needs no such help, but only because `src/index.css` scopes its global `a { color }`
 * rule away from these classes — see the comment there.
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ appearance, fill, size, iconOnly, fullWidth, startIcon, endIcon, className, render, children, ...rest }, ref) =>
    useRender({
      ref,
      render: render ?? <a />,
      props: {
        className: cn(buttonRecipe({ appearance, fill, size, iconOnly, fullWidth }), 'no-underline', className),
        children: (
          <ButtonContent endIcon={endIcon} iconOnly={iconOnly} startIcon={startIcon}>
            {children}
          </ButtonContent>
        ),
        ...rest,
      },
    })
);
